/**
 * VoiceOrderCallClient
 * ---------------------------------------------------------------------
 * Drives one end-to-end "place order by voice" call against the Gemini
 * Live API, from opening the WebSocket with a backend-minted ephemeral
 * token through to hanging up.
 *
 * This class is intentionally framework-agnostic (plain axios + native
 * WebSocket/Web Audio APIs) rather than written against this app's
 * modular-openscriptjs Mediator/event system, so it can be driven from
 * a Mediator, a view, or a quick manual test without any other app
 * wiring. A thin mediator (see src/mediators/VoiceOrderCall.js) is the
 * suggested integration point: it should instantiate this class, wire
 * its `on(...)` events to $e broadcasts, and hand it to whatever view
 * renders the call screen.
 *
 * Usage:
 *   const call = new VoiceOrderCallClient({ apiBaseUrl: routes.base });
 *   call.on('status', (e) => console.log(e.detail.status));
 *   call.on('error', (e) => inform(e.detail.message, STRINGS.ERROR));
 *   await call.start();
 *   // ... later, user taps "hang up" ...
 *   await call.stop('client_ended');
 *
 * IMPORTANT - things flagged during planning that still need a real,
 * live verification pass against the actual Gemini Live API before
 * shipping (the exact wire schema below was taken from Google's docs,
 * not exercised against a live connection during this implementation
 * pass, since this session has no way to run the PHP backend or open a
 * live network socket to Google):
 *   - The exact shape/necessity of the client's initial `setup` message
 *     when connecting with an ephemeral token whose liveConnectConstraints
 *     already lock model/systemInstruction/tools/responseModalities.
 *   - The exact field path for incoming model audio chunks
 *     (assumed here: serverContent.modelTurn.parts[].inlineData).
 *   - Output transcription field, if you want live captions
 *     (see outputAudioTranscription in the session config - not wired
 *     up yet here).
 */
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export class VoiceOrderCallClient extends EventTarget {
    /**
     * @param {Object} opts
     * @param {Object} [opts.requester] This app's `api` Requester (from
     *   src/declarations.js) - preferred, since on native builds it goes
     *   through Capacitor's HTTP plugin (real native network stack, no
     *   WebView CORS/cleartext issues) instead of a plain fetch(). When
     *   omitted, falls back to a plain fetch() against `apiBaseUrl` +
     *   `getAuthToken()`, which is fine for local/web testing.
     *
     *   Tried and reverted: routing session create/end/heartbeat through
     *   plain fetch() instead of `requester` on native, on the theory
     *   that CapacitorHttp was interfering with the WebSocket connection
     *   opened right after (by analogy with
     *   https://github.com/ionic-team/capacitor/issues/7568). That
     *   broke session creation outright on the Android build ("Could not
     *   reach the server") - confirming the WebView's own fetch/XHR
     *   networking has real limitations here that CapacitorHttp exists
     *   to route around, and that issue is a narrower case than ours
     *   (CapacitorHttp's *enabled: true* auto-patch breaking Socket.IO's
     *   HTTP-polling handshake specifically - not applicable to this
     *   app's `enabled: false` config and this class's plain
     *   `new WebSocket(...)`, which never goes through fetch/XHR at
     *   all). Keep using `requester` here; the "socket to Google never
     *   connects, only on native" bug needs a different root cause.
     * @param {string} [opts.apiBaseUrl] Required when `requester` is omitted.
     * @param {() => string|null} [opts.getAuthToken] Returns the bearer token for our own backend when falling back to fetch(). Defaults to reading `auth_token` from localStorage, matching Requester's default.
     */
    constructor({ requester, apiBaseUrl, getAuthToken, sessionsPath } = {}) {
        super();

        if (!requester && !apiBaseUrl) {
            throw new Error(
                "VoiceOrderCallClient requires either `requester` (this app's api object) or `apiBaseUrl`",
            );
        }

        this.requester = requester || null;
        this.apiBaseUrl = apiBaseUrl ? apiBaseUrl.replace(/\/$/, "") : "";
        this.getAuthToken =
            getAuthToken || (() => localStorage.getItem("auth_token"));
        // Matches backendRoutes.js `routes.voiceOrderSessions` - pass that
        // in from the mediator so the path stays centralized there.
        this.sessionsPath = sessionsPath || "/ai/voice-orders/sessions";

        this.session = null; // { session_id, token, ws_url, model, heartbeat_interval_seconds, max_minutes_allowed }
        this.ws = null;
        this.status = "idle"; // idle | starting | connecting | active | ending | ended | error

        this.mediaStream = null;
        this.audioContext = null;
        this.micSourceNode = null;
        this.micProcessorNode = null;

        this.playbackContext = null;
        this.playbackQueueTime = 0;
        this.playbackAnalyser = null;

        // Drives the "AI is speaking" pulse in the UI - sampled off the
        // playback graph (not the mic), throttled, dispatched as an
        // "audiolevel" event with a normalized 0-1 { level }.
        this._audioLevelRAF = null;
        this._audioLevelBuffer = null;
        this._lastAudioLevelEmit = 0;

        this.heartbeatTimer = null;
        this.billedSeconds = 0;
        this.creditsCharged = 0;

        // Set once the agent's own EndCall tool call has triggered a
        // graceful hangup, so a stray second tool call can't schedule it
        // twice - see _scheduleGracefulEnd().
        this._graceEndScheduled = false;

        // Set once the opening-greeting trigger has been sent, so a
        // duplicate setupComplete (shouldn't happen, but be defensive)
        // can't make the agent greet the user twice.
        this._greetingSent = false;

        this._boundVisibility = () => this._onVisibilityChange();
        this._boundPageHide = () => this.stop("client_ended");
    }

    // -----------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------

    async start() {
        this._setStatus("starting");

        // Split into distinct failure points with distinct messages -
        // previously all three stages below reported the exact same
        // generic "Could not start the voice call.", which made it
        // impossible to tell, from the UI alone, which one broke. The
        // underlying `cause` was always attached to the dispatched
        // "error" event but nothing ever logged it - see the
        // console.error added where VoiceOrderCall.js listens for that
        // event - so on native, where there's no easy devtools console,
        // this was effectively a black box.
        //
        // Microphone access is requested FIRST, before the session/token
        // is minted - not just so we don't waste a credit reservation on
        // a call the user can't actually make, but because the ephemeral
        // token's newSessionExpireTime is only ~60 seconds from mint
        // time (see GeminiLiveTokenService::mintEphemeralToken() on the
        // backend - that field is Google's deadline to open a NEW
        // session with the token, separate from how long an
        // already-open connection may last). A first-time RECORD_AUDIO
        // permission prompt is a real, user-paced native OS dialog that
        // can easily eat many seconds on a phone - if it were requested
        // AFTER minting (the original order), a slow tap-through could
        // burn enough of that 60-second window that _connectSocket()
        // below opens the WebSocket with an already-expired token,
        // which Google's server would reject/close outright. Getting
        // the microphone out of the way first means the only thing left
        // after minting is the fast, unattended path straight into
        // _connectSocket().
        try {
            await this._openMicrophone();
        } catch (e) {
            this._error(
                "Could not access the microphone. Check that this app has microphone permission (Android/iOS Settings) and try again.",
                e,
            );
            return;
        }

        try {
            this.session = await this._post(this.sessionsPath);
        } catch (e) {
            this._error(
                "Could not reach the server to start the call. Check your connection and try again.",
                e,
            );
            this._stopMicrophone();
            return;
        }

        try {
            this._connectSocket();
            this._attachLifecycleHandlers();
            this._startHeartbeat();
        } catch (e) {
            this._error("Could not start the voice call.", e);
            await this.stop("error");
        }
    }

    /**
     * @param {string} reason client_ended|error
     */
    async stop(reason = "client_ended") {
        if (this.status === "ended" || this.status === "idle") return;

        this._setStatus("ending");
        this._detachLifecycleHandlers();
        this._stopHeartbeat();

        try {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(
                    JSON.stringify({ realtimeInput: { audioStreamEnd: true } }),
                );
                this.ws.close();
            }
        } catch (e) {
            /* best effort */
        }

        this._stopMicrophone();
        this._stopAudioLevelLoop();

        if (this.session?.session_id) {
            try {
                await this._post(
                    `${this.sessionsPath}/${this.session.session_id}/end`,
                );
            } catch (e) {
                /* best effort - the server-side sweep is the real safety net */
            }
        }

        this._setStatus("ended");
        this.dispatchEvent(
            new CustomEvent("ended", {
                detail: {
                    reason,
                    billedSeconds: this.billedSeconds,
                    creditsCharged: this.creditsCharged,
                },
            }),
        );
    }

    // -----------------------------------------------------------------
    // WebSocket
    // -----------------------------------------------------------------

    _connectSocket() {
        this._setStatus("connecting");

        const url = `${this.session.ws_url}?access_token=${encodeURIComponent(this.session.token)}`;
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            // Minimal setup message - model/systemInstruction/tools/
            // responseModalities/sessionResumption are already locked
            // server-side via the ephemeral token's liveConnectConstraints.
            this.ws.send(
                JSON.stringify({
                    setup: {
                        model: this.session.model?.startsWith("models/")
                            ? this.session.model
                            : `models/${this.session.model}`,
                    },
                }),
            );
            this._setStatus("active");
        };

        this.ws.onmessage = (event) => this._onSocketMessage(event);

        this.ws.onerror = (event) => {
            this._error("Voice call connection error.", event);
        };

        this.ws.onclose = (event) => {
            if (this.status === "active" || this.status === "connecting") {
                // Log the actual close code/reason so a dropped connection
                // is diagnosable from the console alone (the Live API often
                // closes with no application-level error message).
                console.warn(
                    `[VoiceOrderCallClient] WebSocket closed unexpectedly (code=${event.code}, reason="${event.reason || "none"}", wasClean=${event.wasClean}).`,
                );
                // Surface a clean, user-facing message via the "error"
                // event (see the class docblock: the mediator/modal already
                // wires this straight into the visible error state) instead
                // of leaving the call silently dead with nothing but a
                // console warning. The raw Live API close reason isn't
                // something to show the user verbatim - e.g. the
                // "CONTENT_TYPE_AUDIO ... not supported for this model
                // configuration" 1007 close this repo hit while on
                // gemini-2.5-flash-native-audio-preview-12-2025 (a known,
                // unresolved upstream issue correlated with tool calls on
                // native-audio-dialog models - see
                // https://discuss.ai.google.dev/t/live-api-bidigeneratecontent-hard-closures-severe-latency-content-type-audio-error-close-code-1007/179817).
                // We've since switched the live_model config to
                // gemini-3.1-flash-live-preview (see config/gemini.php),
                // which isn't a native-audio-dialog model and hasn't shown
                // this failure - but the clean-message-on-close handling
                // here stays regardless of which model is configured.
                //
                // This deliberately does not attempt to reconnect: this
                // class of drop originates from the Live API's own turn
                // generation (triggered by conversation/tool-call content,
                // not a transient network blip), so a fresh connection
                // with the same in-flight context would likely hit the
                // same failure again - full session-resumption reconnect
                // is tracked as separate future work (see the feature
                // plan's Phase 2).
                this._error(
                    "The call disconnected unexpectedly. Please try again in a moment.",
                    event,
                );
                // Server closed the socket without us asking - finalize on
                // our side too so the UI/heartbeat stop, even though the
                // backend sweep will independently true-up billing.
                this.stop("error");
            }
        };
    }

    /**
     * Kick off the agent's first turn right after setup is acknowledged,
     * so it opens the call with its greeting instead of sitting silent
     * until the user speaks. This is a synthetic client turn, not real
     * user speech - the system prompt tells the agent how to interpret
     * and respond to it.
     */
    _sendGreetingTrigger() {
        if (this._greetingSent) return;
        this._greetingSent = true;

        if (this.ws?.readyState !== WebSocket.OPEN) return;

        this.ws.send(
            JSON.stringify({
                clientContent: {
                    turns: [
                        {
                            role: "user",
                            parts: [
                                {
                                    text: "(The call has just connected. Greet the user now, exactly as instructed in your system prompt.)",
                                },
                            ],
                        },
                    ],
                    turnComplete: true,
                },
            }),
        );
    }

    async _onSocketMessage(event) {
        let raw = event.data;
        // The Live API can send Blob frames for binary audio-bearing
        // messages depending on transport - normalize to text/JSON.
        if (raw instanceof Blob) {
            raw = await raw.text();
        }

        let message;
        try {
            message = JSON.parse(raw);
        } catch (e) {
            return;
        }

        if (message.setupComplete) {
            // The Live API never speaks first on its own - it only ever
            // reacts to user input. To get the agent's opening greeting
            // (see the system prompt's "Opening" instructions), the
            // client has to kick off the first turn itself once setup is
            // acknowledged, with a synthetic cue rather than real user
            // speech.
            this._sendGreetingTrigger();
        }

        if (message.toolCall) {
            await this._handleToolCall(message.toolCall);
        }

        if (message.serverContent?.modelTurn?.parts) {
            for (const part of message.serverContent.modelTurn.parts) {
                if (part.inlineData?.data) {
                    this._playAudioChunk(part.inlineData.data);
                }
                if (part.text) {
                    this.dispatchEvent(
                        new CustomEvent("transcript", {
                            detail: { text: part.text },
                        }),
                    );
                }
            }
        }

        if (message.goAway) {
            this.dispatchEvent(
                new CustomEvent("goaway", { detail: message.goAway }),
            );
            // Phase 2: reconnect using the last sessionResumptionUpdate
            // handle instead of just ending. For now, end gracefully.
            this.stop("error");
        }

        if (message.sessionResumptionUpdate?.newHandle) {
            this._resumptionHandle = message.sessionResumptionUpdate.newHandle;
        }
    }

    /**
     * Tool names that are never dispatched to the backend - they're pure
     * frontend actions (page navigation, opening a view) with no data to
     * fetch and no side effect the server needs to know about. This
     * class stays framework-agnostic (see the class docblock), so it
     * doesn't perform the navigation/event-fire itself - it answers
     * Gemini immediately with a canned success result, and relies on the
     * "toolcall" event it already dispatches below to let
     * src/mediators/VoiceOrderCall.js (which does have the app's router/
     * broker) actually perform the action.
     */
    // How long PickLocation waits for the user to tap "Confirm Location"
    // in the map picker before giving up and telling the agent so it can
    // re-prompt, instead of hanging the call forever.
    static PICK_LOCATION_TIMEOUT_MS = 120000;

    // Tools declared NON_BLOCKING server-side (see
    // App\Ai\VoiceOrderToolRegistry::NON_BLOCKING_TOOLS on the backend -
    // keep this set in sync with that one) - slow enough that leaving
    // them BLOCKING stalled the whole Live API session while waiting on
    // them. `scheduling: "WHEN_IDLE"` on the response is the documented
    // default for a NON_BLOCKING call anyway (the model adds the result
    // to context and answers once it's not already mid-generation,
    // without interrupting whatever it's saying) - set explicitly here
    // rather than relying on the implicit default, since correctness
    // here matters more than saving a few bytes.
    static NON_BLOCKING_TOOLS = new Set(["SearchHelpCenter"]);

    static FRONTEND_ONLY_TOOLS = new Set([
        "NavigateToCart",
        "NavigateToOrderHistory",
        "NavigateToOrderTracking",
        "NavigateToAiCredits",
        "NavigateToCapaWallet",
        "NavigateToSearch",
        "NavigateToBusinesses",
        "NavigateToBusinessProfile",
        "NavigateToNotifications",
        "NavigateToSubscriptions",
        "NavigateToRewards",
        "NavigateToHome",
        "NavigateToProfile",
        "OpenAddressesList",
        "OpenAddressForm",
        "OpenProductView",
        "OpenSubscriptionPlansForm",
        "CloseAddressesList",
        "CloseProductView",
        "CloseSubscriptionPlansForm",
        "OpenPickupDropoffForm",
        "ClosePickupDropoffForm",
    ]);

    async _handleToolCall(toolCall) {
        const functionCalls = toolCall.functionCalls || [];
        const responses = [];
        let agentRequestedEnd = false;

        for (const call of functionCalls) {
            let result;
            try {
                if (call.name === "GetCurrentLocation") {
                    result = await this._getDeviceLocation();
                } else if (call.name === "PickLocation") {
                    result = await this._pickLocationOnMap(call.args);
                } else if (call.name === "InspectDom") {
                    result = this._inspectDom();
                } else if (call.name === "InspectViewport") {
                    result = this._inspectViewport();
                } else if (
                    VoiceOrderCallClient.FRONTEND_ONLY_TOOLS.has(call.name)
                ) {
                    result = JSON.stringify({
                        success: true,
                        message: "Done.",
                        data: {},
                        meta: {},
                    });
                } else {
                    const res = await this._post(
                        `${this.sessionsPath}/${this.session.session_id}/tools/invoke`,
                        {
                            tool: call.name,
                            call_id: call.id,
                            arguments: call.args || {},
                        },
                    );
                    result = res.result;
                }
            } catch (e) {
                result = "This action failed on the server.";
            }

            responses.push({
                id: call.id,
                name: call.name,
                response: { result },
                ...(VoiceOrderCallClient.NON_BLOCKING_TOOLS.has(call.name)
                    ? { scheduling: "WHEN_IDLE" }
                    : {}),
            });

            if (call.name === "EndCall") agentRequestedEnd = true;

            this.dispatchEvent(
                new CustomEvent("toolcall", {
                    detail: { name: call.name, args: call.args },
                }),
            );
        }

        if (this.ws?.readyState === WebSocket.OPEN && responses.length) {
            this.ws.send(
                JSON.stringify({
                    toolResponse: { functionResponses: responses },
                }),
            );
        }

        // The agent calls EndCall right after saying goodbye - don't cut
        // the socket immediately, or the user hears the tail of the
        // sentence get chopped off. Wait for whatever is still queued in
        // the playback buffer to actually finish before hanging up.
        if (agentRequestedEnd) this._scheduleGracefulEnd();
    }

    /**
     * Answers the GetCurrentLocation tool call locally using the
     * device's own geolocation, instead of relaying it to the backend -
     * the server has no way to read a device's GPS (see
     * GetCurrentLocation.php's docblock). Mirrors the native/web
     * branching in src/helpers.js's setCurrentLocationCoordinates(),
     * but stays silent (no loading overlay, no confirmation dialog)
     * since this runs mid voice-call - the user already asked for this
     * out loud, so a modal would just pull focus away from the call.
     *
     * Returns a JSON string in the same {success, message, data, meta}
     * envelope shape as App\Ai\Tools\Response::jsonSerialize(), so the
     * agent reads locally-answered and server-answered tool results
     * the same way.
     */
    async _getDeviceLocation() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            if (!Capacitor.isNativePlatform()) {
                if (!navigator.geolocation) {
                    return envelope(
                        false,
                        "Geolocation is not supported on this device.",
                    );
                }

                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 20000,
                        maximumAge: 10000,
                    });
                });

                return envelope(true, "Got the current location.", {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            }

            const permStatus = await Geolocation.checkPermissions();
            let granted = permStatus.location === "granted";

            if (!granted && permStatus.location === "prompt") {
                const requestResult = await Geolocation.requestPermissions();
                granted = requestResult.location === "granted";
            }

            if (!granted) {
                return envelope(
                    false,
                    "Location access is not granted on this device. Ask the user to enable location permission for Carata in their device settings.",
                );
            }

            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 20000,
                maximumAge: 10000,
            });

            return envelope(true, "Got the current location.", {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });
        } catch (e) {
            return envelope(
                false,
                "Could not get the current location. The user may have denied location access.",
            );
        }
    }

    /**
     * Answers the PickLocation tool call by asking the app (via the
     * mediator listening for the "picklocation" event below) to open the
     * real map picker and wait for the user's confirmed answer, instead
     * of relaying it to the backend - opening app UI isn't something the
     * server can do (see PickLocation.php's docblock).
     *
     * This class stays framework-agnostic (see the class docblock) by
     * never importing the app's broker/event system directly - instead
     * it dispatches a "picklocation" CustomEvent carrying a `respond`
     * callback, the same way "toolcall" already lets the mediator learn
     * about frontend-only actions. The mediator is expected to call
     * `respond(jsonString)` exactly once, whenever the user's answer
     * (or lack of one) is known.
     *
     * A safety timeout guarantees this always eventually resolves even
     * if the user backs out of the picker without confirming (there is
     * currently no separate "cancelled" signal from the picker itself -
     * see the plan's verification notes) or the mediator is somehow
     * never wired up - a `settled` guard makes whichever of the real
     * answer or the timeout fires first win, and ignores the other.
     */
    _pickLocationOnMap(args) {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        // Diagnostic breadcrumb - confirms the PickLocation tool call
        // actually reached this branch (as opposed to, say, silently
        // falling through to the generic backend dispatch because of a
        // name mismatch). If this line is missing from the console on a
        // PickLocation call, the bug is upstream of here (Gemini/the
        // tool-call dispatch), not in the map-opening code below.
        console.debug("[VoiceOrderCallClient] PickLocation called with", args);

        return new Promise((resolve) => {
            let settled = false;

            const finish = (resultJson) => {
                if (settled) return;
                settled = true;
                resolve(resultJson);
            };

            this.dispatchEvent(
                new CustomEvent("picklocation", {
                    detail: { args, respond: finish },
                }),
            );

            setTimeout(() => {
                finish(
                    envelope(
                        false,
                        "The user did not confirm a location in time. Ask again, or try a different description.",
                    ),
                );
            }, VoiceOrderCallClient.PICK_LOCATION_TIMEOUT_MS);
        });
    }

    /**
     * Answers the InspectDom tool call locally - a lightweight text
     * summary of what's on screen (page title, route, a handful of
     * visible headings), not a picture. Deliberately plain DOM APIs only
     * (no openscriptjs coupling), consistent with this class staying
     * framework-agnostic.
     */
    _inspectDom() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            const headings = Array.from(document.querySelectorAll("h1, h2, h3"))
                .map((el) => el.textContent?.trim())
                .filter(Boolean)
                .slice(0, 10);

            return envelope(true, "Got the current screen.", {
                page_title: document.title || null,
                path: window.location?.pathname || null,
                headings,
            });
        } catch (e) {
            return envelope(false, "Could not inspect the screen.");
        }
    }

    /**
     * Answers the InspectViewport tool call locally - screen size and
     * scroll position, so the agent can tell how much of the current
     * page is visible without scrolling.
     */
    _inspectViewport() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            return envelope(true, "Got the viewport.", {
                width: window.innerWidth,
                height: window.innerHeight,
                scroll_x: window.scrollX,
                scroll_y: window.scrollY,
                document_height: document.documentElement?.scrollHeight ?? null,
            });
        } catch (e) {
            return envelope(false, "Could not read the viewport.");
        }
    }

    /**
     * Hang up shortly after the agent's own EndCall tool call, once its
     * queued speech has finished playing (plus a small trailing buffer in
     * case one more audio chunk is still in flight). Bounded by a hard
     * cap so a stuck/never-draining queue can't leave the call open
     * forever.
     */
    _scheduleGracefulEnd() {
        if (this._graceEndScheduled) return;
        this._graceEndScheduled = true;

        const deadline = Date.now() + 8000;
        const POLL_MS = 200;
        const TRAILING_BUFFER_MS = 500;

        const check = () => {
            if (this.status === "ended" || this.status === "idle") return;

            const ctx = this.playbackContext;
            const stillPlaying =
                ctx && ctx.currentTime < this.playbackQueueTime;

            if (stillPlaying && Date.now() < deadline) {
                setTimeout(check, POLL_MS);
                return;
            }

            setTimeout(() => this.stop("client_ended"), TRAILING_BUFFER_MS);
        };

        check();
    }

    // -----------------------------------------------------------------
    // Microphone capture -> realtimeInput
    // -----------------------------------------------------------------

    async _openMicrophone() {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                // sampleRate: 16000,
                echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
            },
        });

        this.audioContext = new (
            window.AudioContext || window.webkitAudioContext
        )({ sampleRate: 16000 });
        this.micSourceNode = this.audioContext.createMediaStreamSource(
            this.mediaStream,
        );

        // ScriptProcessorNode is deprecated but has the broadest WebView
        // compatibility for this Capacitor app; swap for an AudioWorklet
        // if/when the minimum supported WebView version allows it.
        const bufferSize = 4096;
        this.micProcessorNode = this.audioContext.createScriptProcessor(
            bufferSize,
            1,
            1,
        );

        this.micProcessorNode.onaudioprocess = (event) => {
            if (
                this.status !== "active" ||
                !this.ws ||
                this.ws.readyState !== WebSocket.OPEN
            ) {
                return;
            }

            const input = event.inputBuffer.getChannelData(0);
            const pcm16 = VoiceOrderCallClient._floatTo16BitPCM(input);
            const base64 = VoiceOrderCallClient._arrayBufferToBase64(
                pcm16.buffer,
            );

            this.ws.send(
                JSON.stringify({
                    realtimeInput: {
                        audio: {
                            mimeType: "audio/pcm;rate=16000",
                            data: base64,
                        },
                    },
                }),
            );
        };

        this.micSourceNode.connect(this.micProcessorNode);
        // Some browsers require the processor node connected to a
        // destination to actually fire onaudioprocess.
        this.micProcessorNode.connect(this.audioContext.destination);
    }

    _stopMicrophone() {
        try {
            this.micProcessorNode?.disconnect();
            this.micSourceNode?.disconnect();
            this.mediaStream?.getTracks().forEach((t) => t.stop());
            this.audioContext?.close();
        } catch (e) {
            /* best effort */
        }
    }

    // -----------------------------------------------------------------
    // Audio playback (24kHz PCM chunks from the model)
    // -----------------------------------------------------------------

    _playAudioChunk(base64Data) {
        if (!this.playbackContext) {
            this.playbackContext = new (
                window.AudioContext || window.webkitAudioContext
            )({ sampleRate: 24000 });
            this.playbackQueueTime = this.playbackContext.currentTime;

            // Every chunk routes through this one analyser on its way to
            // the speakers, so we can read back how loud the agent's
            // voice actually is right now (not just "a chunk arrived").
            this.playbackAnalyser = this.playbackContext.createAnalyser();
            this.playbackAnalyser.fftSize = 256;
            this.playbackAnalyser.smoothingTimeConstant = 0.6;
            this.playbackAnalyser.connect(this.playbackContext.destination);

            this._ensureAudioLevelLoop();
        }

        const bytes = VoiceOrderCallClient._base64ToArrayBuffer(base64Data);
        const pcm16 = new Int16Array(bytes);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
            float32[i] = pcm16[i] / 32768;
        }

        const buffer = this.playbackContext.createBuffer(
            1,
            float32.length,
            24000,
        );
        buffer.copyToChannel(float32, 0);

        const source = this.playbackContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.playbackAnalyser);

        const startAt = Math.max(
            this.playbackQueueTime,
            this.playbackContext.currentTime,
        );
        source.start(startAt);
        this.playbackQueueTime = startAt + buffer.duration;
    }

    /**
     * Samples the playback analyser on every animation frame and, at
     * most every ~80ms, dispatches an "audiolevel" event with a
     * normalized { level } (0 = silent, 1 = loud) reflecting how loud
     * the agent's voice is *right now*. Runs continuously once the
     * first audio chunk arrives - reads back near 0 whenever the agent
     * isn't actively speaking, which is the correct/desired resting
     * state for a "listening" pulse.
     */
    _ensureAudioLevelLoop() {
        if (this._audioLevelRAF) return;

        const tick = () => {
            if (!this.playbackAnalyser) {
                this._audioLevelRAF = requestAnimationFrame(tick);
                return;
            }

            if (
                !this._audioLevelBuffer ||
                this._audioLevelBuffer.length !== this.playbackAnalyser.fftSize
            ) {
                this._audioLevelBuffer = new Uint8Array(
                    this.playbackAnalyser.fftSize,
                );
            }

            this.playbackAnalyser.getByteTimeDomainData(this._audioLevelBuffer);

            let sumSquares = 0;
            for (let i = 0; i < this._audioLevelBuffer.length; i++) {
                const centered = (this._audioLevelBuffer[i] - 128) / 128;
                sumSquares += centered * centered;
            }
            const rms = Math.sqrt(sumSquares / this._audioLevelBuffer.length);
            // Typical speech RMS on this scale is small - boost it into a
            // usable 0-1 range for the UI.
            const level = Math.min(1, rms * 4);

            const now =
                typeof performance !== "undefined"
                    ? performance.now()
                    : Date.now();
            if (now - this._lastAudioLevelEmit > 80) {
                this._lastAudioLevelEmit = now;
                this.dispatchEvent(
                    new CustomEvent("audiolevel", { detail: { level } }),
                );
            }

            this._audioLevelRAF = requestAnimationFrame(tick);
        };

        this._audioLevelRAF = requestAnimationFrame(tick);
    }

    _stopAudioLevelLoop() {
        if (this._audioLevelRAF) {
            cancelAnimationFrame(this._audioLevelRAF);
            this._audioLevelRAF = null;
        }
        this._lastAudioLevelEmit = 0;
        this.dispatchEvent(
            new CustomEvent("audiolevel", { detail: { level: 0 } }),
        );
    }

    // -----------------------------------------------------------------
    // Heartbeat / billing
    // -----------------------------------------------------------------

    _startHeartbeat() {
        const intervalMs =
            (this.session.heartbeat_interval_seconds || 15) * 1000;
        this.heartbeatTimer = setInterval(() => this._heartbeat(), intervalMs);
    }

    _stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    async _heartbeat() {
        if (!this.session?.session_id || this.status !== "active") return;

        try {
            const res = await this._post(
                `${this.sessionsPath}/${this.session.session_id}/heartbeat`,
            );

            this.billedSeconds = res.billed_seconds;
            this.creditsCharged = res.credits_charged;

            this.dispatchEvent(
                new CustomEvent("heartbeat", {
                    detail: {
                        billedSeconds: this.billedSeconds,
                        creditsCharged: this.creditsCharged,
                    },
                }),
            );

            if (res.should_end) {
                await this.stop("client_ended");
            }
        } catch (e) {
            // A missed heartbeat is not fatal - the next one will catch up,
            // and the backend sweep is the actual correctness guarantee.
        }
    }

    // -----------------------------------------------------------------
    // App/tab lifecycle - best-effort "end on close", not the safety net
    // -----------------------------------------------------------------

    _attachLifecycleHandlers() {
        document.addEventListener("visibilitychange", this._boundVisibility);
        window.addEventListener("pagehide", this._boundPageHide);

        // Capacitor's App plugin, if present, tells us about the native
        // app being backgrounded/killed - hook it defensively since we
        // can't be certain of its exact import wiring in this bundle.
        const CapApp = window.Capacitor?.Plugins?.App;
        if (CapApp?.addListener) {
            CapApp.addListener("appStateChange", ({ isActive }) => {
                if (!isActive) this.stop("client_ended");
            });
        }
    }

    _detachLifecycleHandlers() {
        document.removeEventListener("visibilitychange", this._boundVisibility);
        window.removeEventListener("pagehide", this._boundPageHide);
    }

    _onVisibilityChange() {
        if (document.visibilityState === "hidden" && this.status === "active") {
            // Don't hang up immediately on a brief tab switch; the
            // heartbeat/sweep design tolerates a short gap. Only bail once
            // we've been hidden long enough that the session would go
            // stale anyway.
            clearTimeout(this._hiddenTimeout);
            this._hiddenTimeout = setTimeout(() => {
                if (document.visibilityState === "hidden")
                    this.stop("client_ended");
            }, 30000);
        } else if (document.visibilityState === "visible") {
            clearTimeout(this._hiddenTimeout);
        }
    }

    // -----------------------------------------------------------------
    // HTTP + helpers
    // -----------------------------------------------------------------

    async _post(path, body) {
        if (this.requester) {
            const response = await this.requester.post(path, body);
            const data =
                typeof response?.json === "function"
                    ? await response.json()
                    : response;

            if (response?.ok === false) {
                throw new Error(data?.message || `Request to ${path} failed`);
            }

            return data?.data ?? data;
        }

        const token = this.getAuthToken();

        const response = await fetch(`${this.apiBaseUrl}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body ? JSON.stringify(body) : undefined,
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(data.message || `Request to ${path} failed`);
        }

        return data.data ?? data;
    }

    _setStatus(status) {
        this.status = status;
        this.dispatchEvent(new CustomEvent("status", { detail: { status } }));
    }

    _error(message, cause) {
        this.dispatchEvent(
            new CustomEvent("error", { detail: { message, cause } }),
        );
    }

    static _floatTo16BitPCM(float32Array) {
        const out = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            const s = Math.max(-1, Math.min(1, float32Array[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    static _arrayBufferToBase64(buffer) {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    static _base64ToArrayBuffer(base64) {
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }
}
